//#region import react
import { pdf } from "@react-pdf/renderer";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
//#endregion

//#region import component
import CustomPdf from "../../Components/Pdf/CustomPdf/CustomPdf.component";
//#endregion

//#region import constants
import { LANGUAGE, TYPE_NOTATION_CODE } from "../../Constants/SettingsConstants";
import { TRANSLATIONS } from "../../Constants/Translations";
import { ACREOS_TEMPLATE } from "../Center/InscriptionManagementPage/IncriptionManagementPage.constants";
//#endregion

//#region import store
import { setNotationType } from "../../ReduxStore/settingSlice";
import { setLanguage } from "../../ReduxStore/translationSlice";
//#endregion

//#region import services
import { authenticate } from "../../Services/AuthenticateService";
import { sendEmailWithManyPdfs } from "../../Services/MailService";
import { getSessionDetails, getStudentPdfResults } from "../../Services/SessionService";
import { getCenterSettings, GetPdfInfos, getTemplatePdf, getUserSettings } from "../../Services/SettingsService";
import { getTrainerCenterDto } from "../../Services/TrainerService";
//#endregion

//#region import functions
import { getTranslations, reloadEnginesTranslations, reloadGSTATranslations } from "../../Helper/TranslationController";
//#endregion

//#region const
const url = new URL(window.location.href);
const centerId = url.searchParams.get("centerId");
const trainerUserId = url.searchParams.get("trainerUserId");
const login = url.searchParams.get("login");
const hashedSecret = url.searchParams.get("hashedSecret");
const sessionId = url.searchParams.get("sessionId");
//#endregion

async function blobFromReactPdf(el) {
  let blob;
  try {
    blob = await pdf(el).toBlob();
  } catch (e) {
    console.error("❌ Error generate blob", {
      name: e?.name,
      message: e?.message,
      stack: e?.stack,
    });
  }
  return blob;
}

function fileNameFor(person) {
  const norm = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const safe = (s) => norm(String(s)).replace(/[^a-z0-9_-]+/gi, "_");
  return `${safe(person.lastName)}_${safe(person.firstName)}.pdf`;
}

export default function BatchPdfGeneratorPage() {
  //#region hook
  const translations = useSelector((state) => state.translationSlice.translations);
  const notationType = useSelector((state) => state.settingSlice.notationType);
  const customFields = useSelector((state) => state.studentSlice.customFields);
  //#endregion

  //#region states
  const [isLogin, setIsLogin] = useState(false);
  const [centerName, setCenterName] = useState("");
  const [selectedPdfTemplate, setSelectedPdfTemplate] = useState({});
  const [trainerEmail, setTrainerEmail] = useState("");
  const [students, setStudents] = useState([]);
  const [sessionName, setSessionName] = useState("");
  //#endregion

  const dispatch = useDispatch();

  //#region useQuery
  const { isSuccess: isSuccessUserSettings, isFetching: isFetchingUserSettings } = useQuery({
    queryKey: ["userSettings"],
    queryFn: () => getUserSettings(centerId, trainerUserId, LANGUAGE),
    enabled: !!centerId && !!trainerUserId && !!LANGUAGE && isLogin,
    meta: {
      onSuccess: async (data) => await onGetUserSettingsSuccess(data.value),
      onerror: (e) => {
        console.error("❌ Error get user setting language", {
          name: e?.name,
          message: e?.message,
          stack: e?.stack,
        });
      },
    },
  });

  const { isSuccess: isSuccessCenterNotationType, isFetching: isFetchingCenterNotationType } = useQuery({
    queryKey: ["centerNotationType"],
    queryFn: () => getCenterSettings(centerId, TYPE_NOTATION_CODE),
    enabled: (notationType === undefined || notationType === null) && isLogin,
    meta: {
      onSuccess: (data) => {
        dispatch(setNotationType(data.value));
      },
      onerror: (e) => {
        console.error("❌ Error get center notation type", {
          name: e?.name,
          message: e?.message,
          stack: e?.stack,
        });
      },
    },
  });

  const { mutate: authentication, data: authenticateResponse } = useMutation({
    mutationKey: ["authenticate"],
    mutationFn: () => authenticate(login, hashedSecret),
    onSuccess: () => {
      setIsLogin(true);
      localStorage.setItem("isLogIn", isLogin);
    },
    onerror: (e) => {
      console.error("❌ Error authenticate", {
        name: e?.name,
        message: e?.message,
        stack: e?.stack,
      });
    },
    enabled: false,
  });

  useQuery({
    queryKey: ["trainerCenterDto"],
    queryFn: () => getTrainerCenterDto(authenticateResponse.id),
    meta: {
      onSuccess: (trainer) => {
        setCenterName(trainer.activeCenter.name);
        setTrainerEmail(trainer.email);
      },
      onerror: (e) => {
        console.error("❌ Error get trainer center", {
          name: e?.name,
          message: e?.message,
          stack: e?.stack,
        });
      },
    },
    enabled: isLogin,
  });

  useQuery({
    queryKey: ["templatePdf"],
    queryFn: () => getTemplatePdf(centerId),
    meta: {
      onSuccess: (templatesPdfGetted) => {
        const acreosTemplate = templatesPdfGetted.find((pdfTemplate) => pdfTemplate.name === ACREOS_TEMPLATE);
        setSelectedPdfTemplate(
          templatesPdfGetted.find((pdfTemplate) => pdfTemplate.isDefault === true) ?? acreosTemplate,
        );
      },
      onerror: (e) => {
        console.error("❌ Error get template pdf", {
          name: e?.name,
          message: e?.message,
          stack: e?.stack,
        });
      },
    },
    enabled: isLogin,
  });

  const {
    refetch: refetchSessionDetails,
    data: sessionDetails,
    isSuccess: isSessionDetailsSucces,
  } = useQuery({
    queryKey: ["sessionDetails"],
    queryFn: () => getSessionDetails(centerId, sessionId),
    meta: {
      onSuccess: (data) => {
        setStudents(data?.students);
        setSessionName(data?.name);
        console.log("Session details retrieved ✅");
      },
      onerror: (e) => {
        console.error("❌ Error get dession details", {
          name: e?.name,
          message: e?.message,
          stack: e?.stack,
        });
      },
    },
    enabled: false,
  });

  const { refetch: refetchPdfInfos, isSuccess: isPdfInfosSuccess } = useQuery({
    queryKey: ["pdfInfos"],
    queryFn: () => GetPdfInfos(centerId),
    meta: {
      onSuccess: () => {
        console.log("Pdf infos retrieved ✅");
      },
      onerror: (e) => {
        console.error("❌ Error get pdf infos", {
          name: e?.name,
          message: e?.message,
          stack: e?.stack,
        });
      },
    },
    enabled: false,
  });
  //#endregion

  //#region useEffect
  useEffect(() => {
    (async () => {
      try {
        authentication();
      } catch (e) {
        console.error("❌ Authentication failed:", e);
      }
    })();
  }, []);

  useEffect(() => {
    const renderPdfWhenReady = async () => {
      if (
        isLogin &&
        isSuccessCenterNotationType &&
        isSuccessUserSettings &&
        !isFetchingUserSettings &&
        !isFetchingCenterNotationType &&
        getTranslations("120_m", translations) !== "120_m"
      ) {
        await generatePdf();
      }
    };

    renderPdfWhenReady();
  }, [
    isLogin,
    isSuccessCenterNotationType,
    isSuccessUserSettings,
    !isFetchingUserSettings,
    !isFetchingCenterNotationType,
    getTranslations("120_m", translations),
    isSessionDetailsSucces,
    isPdfInfosSuccess,
  ]);
  //#endregion

  //#region functions
  const onGetUserSettingsSuccess = async (language) => {
    try {
      dispatch(setLanguage(language));
      await reloadEnginesTranslations(dispatch, centerId, language);
      await reloadGSTATranslations(dispatch, language);
    } catch (e) {
      console.error(e);
    }
  };

  const generatePdf = async () => {
    // 1) Récupère les stagiaires
    if (!isSessionDetailsSucces) {
      await refetchSessionDetails();
    }

    if (!isPdfInfosSuccess) {
      await refetchPdfInfos();
    }

    if (!isSessionDetailsSucces || !isPdfInfosSuccess) {
      return;
    }

    // 2) Prépare les infos PDF
    const pdfInfos = await GetPdfInfos(centerId);
    const params = {
      templateId: selectedPdfTemplate.id,
      startPeriod: sessionDetails?.startDate,
      endPeriod: sessionDetails?.endDate,
    };

    // 3) Génère les PDF et blobs
    const files = await Promise.all(
      students.map(async (student) => {
        const data = await getStudentPdfResults(centerId, student.id, sessionId, params);
        const content = (
          <CustomPdf
            pdfData={{
              ...data,
              firstName: student.firstName,
              lastName: student.lastName,
              center: centerName,
              sessionName: sessionName,
            }}
            accessStart={params.startPeriod}
            accessEnd={params.endPeriod}
            sessionDates={true}
            translations={translations}
            notationType={notationType}
            customFields={customFields}
            pdfTemplate={selectedPdfTemplate}
            pdfInfos={pdfInfos}
          />
        );
        const blob = await blobFromReactPdf(content);
        return { filename: fileNameFor(student), blob };
      }),
    );

    // 4) Envoie l'email
    if (files.length > 0) {
      try {
        const objectTranslation = getTranslations(TRANSLATIONS.MAIL_PDF_SESSION_OBJECT, translations);
        const object = objectTranslation.replace("{}", sessionDetails.name);
        const bodyTranslation = getTranslations(TRANSLATIONS.MAIL_PDF_SESSION_BODY, translations);
        const html = bodyTranslation.replace("{}", sessionDetails.name);
        await sendEmailWithManyPdfs(trainerEmail, object, files, html);
      } catch (e) {
        console.error("❌ Error send mail", {
          name: e?.name,
          message: e?.message,
          stack: e?.stack,
        });
      }
    } else {
      console.error("No files to send ❌");
    }
  };
  //#endregion

  return <main></main>;
}
